<template>
  <header class="bg-black text-white bg-opacity-95">
    <div class="flex relative justify-between items-center mb-0 md:mb-2 px-4 py-2 md:py-4">
      <!-- Left side: Back and Home buttons -->
      <div class="flex items-center">
        <!-- Back button -->
        <button v-if="!isHome" @click="goBack" class="flex md:hidden flex-shrink-0 rounded-full bg-white bg-opacity-10 hover:bg-opacity-20 p-2 transition-all duration-200">
          <font-awesome-icon icon="arrow-left" class="h-6 w-6 text-white" />
        </button>

        <!-- Home button -->
        <button
          @click="goActualHome"
          class="hidden md:flex flex-shrink-0 rounded-full px-2 py-1 text-sm transition-all duration-200"
          type="button"
        >
          <font-awesome-icon icon="home" class="h-6 w-6 text-white hover:text-orange-500" />        
        </button>
        
        <button
          v-if="showOriginButton"
          @click="goPartnerHome"
          class="hidden md:flex flex-shrink-0 rounded-full px-2 py-1 text-sm transition-all duration-200"
          type="button"
        >
          <ExitArrowIcon fill="currentColor" class="h-6 w-6 text-white hover:text-orange-500" />
        </button>

      </div>
      <!-- Home button - Mobile (logo) -->
      <div
        class="absolute flex md:hidden items-center gap-2"
        :class="isHome ? 'left-2' : 'left-1/2 -translate-x-1/2'"
      >
        <button
          @click="goActualHome"
          class="flex h-10 rounded-full flex-shrink-0 p-2 bg-white bg-opacity-10 hover:bg-opacity-20"
          type="button"
        >
          <font-awesome-icon icon="home" class="h-6 w-6 text-white hover:text-orange-500" />
        </button>

        <button
          v-if="showOriginButton"
          @click="goPartnerHome"
          class="flex h-10 rounded-full flex-shrink-0 p-2 bg-white bg-opacity-10 hover:bg-opacity-20 transition-all duration-200"
          type="button"
        >
          <ExitArrowIcon fill="currentColor" class="h-6 w-6 text-white hover:text-orange-500" />
        </button>
      </div>

      <!-- Right side: Auth, Favorites, and Search -->
      <div class="flex items-center space-x-2">
        <!-- Desktop Authentication Section (hidden on mobile) -->
        <div class="hidden md:flex items-center">
          <div v-if="isLoading" class="flex items-center">
            <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          </div>

          <div v-else-if="isAuthenticated" class="flex items-center space-x-2">
            <div class="flex items-center space-x-2">
              <img v-if="user?.picture" :src="user.picture" :alt="user.name || 'User'" class="w-8 h-8 rounded-full" />
              <span class="text-sm">
                {{ user?.name || user?.email }}
              </span>
            </div>
            <button @click="handleLogout"
              class="px-3 py-1 text-sm bg-red-600 hover:bg-red-700 rounded transition-colors duration-200">
              Sign Out
            </button>
          </div>
          <div v-else>
            <!-- Sign In button -->
            <button @click="handleLogin"
              class="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 rounded transition-colors duration-200">
              Sign In
            </button>
          </div>
        </div>

        <!-- Favorites toggle -->
        <button v-if="isAuthenticated && configStore.features.includes(FEATURES.REGISTRATION_FAVORITES)" 
          type="button" 
          class="rounded-full h-10 bg-white bg-opacity-10 md:bg-transparent hover:text-red-500 p-2 transition-all duration-200" 
          :class="isFavFilter ? 'text-red-500' : 'text-white'"
          @click="toggleFavoritesFilter" 
          aria-pressed="isFavFilter"
          :title="isFavFilter ? 'Show all Videos' : 'Show Favorites Only'">
          <font-awesome-icon :icon="['fas', 'heart']" class="h-6 w-6" />
        </button>

        <!-- Search -->
        <form v-if="showSearchIcon" @submit.prevent="handleSearch" ref="searchForm"
          class="relative flex items-center group">
          <button type="button" class="rounded-full h-10 bg-white bg-opacity-10 md:bg-transparent  p-2 transition-all duration-200" @click="toggleSearch">
            <font-awesome-icon icon="magnifying-glass" class="h-6 w-6 text-white hover:text-orange-500 transition-transform duration-300"
              :class="searchActive ? 'rotate-90' : 'rotate-0'" />
          </button>

          <div
            class="absolute right-0 top-full mt-2 bg-white rounded shadow transition-all duration-300 ease-in-out transform origin-top-right"
            :class="searchActive ? 'scale-100 opacity-100' : 'scale-0 opacity-0'">
            <div class="flex items-center p-2">
              <input v-model="searchQuery" ref="searchInput" type="text" placeholder="Search..."
                class="px-3 py-2 rounded text-black w-40" />
              <button type="submit"
                class="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 whitespace-nowrap ms-1">
                Search
              </button>
              <button type="button" v-if="searchQuery" @click="clearSearch" class="text-gray-500 hover:text-red-500 ms-2"
                title="Clear search">
                <font-awesome-icon icon="x" class="ml-1" />
              </button>
            </div>
          </div>
        </form>

        <!-- Hamburger Menu Button (Mobile Only) -->
        <button 
          @click="toggleSidebar"
          class="flex md:hidden rounded-full bg-white bg-opacity-10 hover:bg-opacity-20 p-2 transition-all duration-200">
          <font-awesome-icon icon="bars" class="h-6 w-6 text-white" />
        </button>
      </div>
    </div>

    <!-- Mobile Sidebar -->
    <div 
      @click="closeSidebar"
      class="fixed inset-0 z-50 md:hidden transition-opacity duration-300"
      :class="sidebarOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'">
      <!-- Backdrop -->
      <div class="absolute inset-0 bg-black bg-opacity-50"></div>
      
      <!-- Sidebar -->
      <div 
        @click.stop
        class="absolute right-0 top-0 h-full w-64 bg-black shadow-lg transform transition-transform duration-300 ease-in-out"
        :class="sidebarOpen ? 'translate-x-0' : 'translate-x-full'">
        
        <!-- Close Button -->
        <div class="flex justify-end p-4">
          <button @click="closeSidebar" class="rounded-full h-10 bg-white bg-opacity-10 hover:bg-opacity-20 p-2 transition-all">
            <font-awesome-icon icon="times" class="h-6 w-6 text-white" />
          </button>
        </div>

        <!-- User Profile Section -->
        <div class="px-6 py-4">
          <div v-if="isLoading" class="flex justify-center items-center py-8">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
          </div>

          <div v-else-if="isAuthenticated" class="flex flex-col items-center space-y-4">
            <!-- Profile Picture -->
            <img v-if="user?.picture" :src="user.picture" :alt="user.name || 'User'" class="w-20 h-20 rounded-full" />
            
            <!-- Display Name -->
            <div class="text-center">
              <p class="text-white font-semibold text-lg max-w-52 truncate">{{ user?.name || user?.email }}</p>
            </div>

            <!-- Logout Button -->
            <button @click="handleLogout"
              class="w-full px-4 py-2 text-sm bg-red-600 hover:bg-red-700 rounded transition-colors duration-200">
              Sign Out
            </button>
          </div>

          <div v-else class="flex flex-col items-center space-y-4 py-8">
            <!-- Sign In button -->
            <button @click="handleLogin"
              class="w-full px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 rounded transition-colors duration-200">
              Sign In
            </button>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>


<script setup>
import { useRouter, useRoute } from 'vue-router'
import { ref, onMounted, onBeforeUnmount, computed, nextTick, watch } from 'vue'
import { useVideoStore } from '../store/videoStore';
import { useAuth } from '../composables/useAuth';
import { useConfigStore } from '../store/configStore'
import { FEATURES } from '../constants/features'
import ExitArrowIcon from './icons/ExitArrowIcon.vue'

const defaultFeedId = import.meta.env.VITE_DEFAULT_FEED_ID
const feedId = computed(() => route.params.partnerId || defaultFeedId)
const route = useRoute()
const videoStore = useVideoStore()
const { isAuthenticated, isLoading, user, login, logout } = useAuth()
const searchInput = ref(null)
const router = useRouter()
const configStore = useConfigStore()
const isInSearch = ref(false)

//fav filter
const isFavFilter = computed(() => route.query.favs === '1')

//know if a home page or a partner page to show search icon
const showSearchIcon = computed(() => {
  const { params, path } = route
  const segments = path.split('/').filter(segment => segment)
  const isHome = segments.length === 0
  const isPartnerRoot = params.partnerId && segments.length === 1
  return isHome || isPartnerRoot
})

const currentPartnerId = computed(() => {
  const partnerId = route.params.partnerId
  if (Array.isArray(partnerId)) return partnerId[0] || null
  return partnerId || null
})

const originPartnerId = computed(() => {
  const origin = route.query.originPartnerId
  if (Array.isArray(origin)) return origin[0] || null
  return origin || null
})

const originPartnerMismatch = computed(() => (
   // We don't directly do originPartnerId?.value !== currentPartnerId?.value because we want to check 
  // if they have values and are different, if one is null and the other is not then it would fail that way
  originPartnerId.value && currentPartnerId.value && originPartnerId.value !== currentPartnerId.value
))

const isHomeView = computed(() => route.name === 'home')

const hasHomeFiltersActive = computed(() => (
  isInSearch.value || isFavFilter.value
))

const showOriginButton = computed(() => (
  originPartnerMismatch.value && (!isHomeView.value || hasHomeFiltersActive.value)
))

const isHome = computed(() => (route.path === '/' || route.path === `/${import.meta.env.VITE_DEFAULT_FEED_ID}`) && (!isInSearch.value) && !isFavFilter.value)

const searchQuery = ref('')
const searchActive = ref(false)
const searchForm = ref(null)
const sidebarOpen = ref(false)

const handleLogin = () => {
  login({partnerId: route.params.partnerId})
  closeSidebar()
}

const goBack = () => {
  if (route.path === '/') {
    clearSearch()
    deactivateFavoritesFilter()
  } else {
    router.back()
  }
}

function goActualHome() {
  router.push({
    name: 'home',
    params: { partnerId: originPartnerId.value || route.params?.partnerId }
  })
  closeSidebar()
}

function goPartnerHome() {
  if (!originPartnerId.value) return

  clearSearch()

  router.push({
    name: 'home',
    params: { partnerId: route.params?.partnerId },
    query: {
      originPartnerId: originPartnerId.value,
    },
  })
  closeSidebar()
}

const handleLogout = () => {
  logout({partnerId: route.params.partnerId})
  closeSidebar()
}

const toggleSidebar = () => {
  sidebarOpen.value = !sidebarOpen.value
}

const closeSidebar = () => {
  sidebarOpen.value = false
}

async function handleSearch(e) {
  e.preventDefault()
  await videoStore.loadVideos(feedId.value, searchQuery.value)
  searchActive.value = false
  isInSearch.value = Boolean(searchQuery.value)
}

function clearSearch() {
  searchQuery.value = ''
  videoStore.loadVideos(feedId.value, '', true)
  isInSearch.value = false
}

function toggleSearch() {
  searchActive.value = !searchActive.value
  if (searchActive.value) {
    nextTick(() => {
      searchInput.value?.focus();
    });
  }
}

function handleClickOutside(event) {
  if (searchForm.value && !searchForm.value.contains(event.target)) {
    searchActive.value = false
  }
}

// Prevent body scrolling when sidebar is open
watch(sidebarOpen, (isOpen) => {
  if (isOpen) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
})

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
  // Clean up body overflow on unmount
  document.body.style.overflow = ''
})

function toggleFavoritesFilter() {
  const on = route.query.favs === '1'
  router.push({
    name: 'home',
    params: { partnerId: route.params.partnerId },
    query: { ...route.query, favs: on ? undefined : '1' } // remove when off
  })
}

function deactivateFavoritesFilter() {
  router.push({
    name: 'home',
    params: { partnerId: route.params.partnerId },
    query: { ...route.query, favs: undefined }
  })
}

</script>


<style scoped></style>
